import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BannedResultCard from "@/components/BannedResultCard";
import SafeResultCard from "@/components/SafeResultCard";
import SuggestionsCard from "@/components/SuggestionsCard";
import { publicApi } from "@/lib/api";
import { debug } from "@/lib/log";
import { Search, ShieldAlert, Loader2, ScanLine, PillBottle, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import PhotoOcrModal from "@/components/PhotoOcrModal";

export default function Home() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { query, results, is_banned } | null
  const [drugs, setDrugs] = useState([]);
  const [scannerCooldown, setScannerCooldown] = useState(false);
  const [photoOcrOpen, setPhotoOcrOpen] = useState(false);

  // Memoised so the effect below has a stable dependency and never loops.
  const loadDrugs = useCallback(async () => {
    try {
      const data = await publicApi.recent(1000);
      setDrugs(data.results || []);
    } catch (err) {
      debug("loadDrugs failed:", err);
    }
  }, []);

  useEffect(() => {
    loadDrugs();
    // Refresh when the tab regains focus (e.g. after editing in /admin/edit)
    const onFocus = () => loadDrugs();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadDrugs]);

  const doSearch = async (q) => {
    const term = (q ?? query).trim();
    if (!term) {
      toast.error("يرجى إدخال اسم الدواء");
      return;
    }
    if (term.length > 100) {
      toast.error("النص طويل جدًا (100 حرف كحد أقصى)");
      return;
    }
    if (scannerCooldown) return;
    setScannerCooldown(true);
    setTimeout(() => setScannerCooldown(false), 1000);
    setLoading(true);
    setResult(null);
    try {
      const data = await publicApi.search(term);
      setResult(data);
      // scroll to result
      setTimeout(() => {
        const el = document.getElementById("search-result");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        toast.error("Too many requests. Please wait a moment and try again.");
      } else {
        toast.error("حدث خطأ أثناء البحث. حاول مرة أخرى.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoOcrConfirm = (payload) => {
    setPhotoOcrOpen(false);
    // Confirmed OCR match: payload is a drug object with an `id` → open the
    // exact record directly, bypassing fuzzy/DeepSeek. Uncertain OCR (no
    // validated DB hit): payload is a plain string → run the normal text
    // search pipeline exactly like a typed query.
    if (payload && typeof payload === "object" && payload.id) {
      openKnownDrug(payload);
      return;
    }
    const name = typeof payload === "string" ? payload : "";
    if (!name) return;
    setQuery(name);
    doSearch(name);
  };

  // Direct-open helper for clicks on a KNOWN medicine record (recent list,
  // exact search results, suggestions, confirmed OCR match). Enriches
  // lightweight objects (`{id, name, name_en}` returned by search / OCR)
  // from the local `drugs` cache so the BannedResultCard has `reason` and
  // `doctor_note`. Bypasses the fuzzy pipeline — no re-searching a display
  // name like "ليريكا، بريجابالين" against itself. Falls back to `doSearch`
  // only for legacy records missing an id.
  const openKnownDrug = (drug) => {
    if (!drug || !drug.id) {
      if (drug && drug.name) doSearch(drug.name);
      return;
    }
    // If the payload is a slim search-result object, enrich it from the
    // full drugs list already loaded on this page.
    const full = drugs.find((x) => x.id === drug.id) || drug;
    const primary =
      (Array.isArray(full.names_ar) && full.names_ar[0]) ||
      (Array.isArray(full.names_en) && full.names_en[0]) ||
      full.name || "";
    setQuery(primary);
    setResult({
      query: primary,
      count: 1,
      results: [full],
      is_banned: true,
      suggestions: [],
    });
    setTimeout(() => {
      const el = document.getElementById("search-result");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      {/* Hero + Search */}
      <section className="relative overflow-hidden border-b border-slate-200 grain-bg">
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
          <div className="fade-up inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-bold tracking-tight px-4 py-2 rounded-full mb-8">
            <ShieldAlert className="w-4 h-4" />
            <span>منصة رقابية للأدوية المقيدة</span>
          </div>

          <h1
            data-testid="hero-title"
            className="fade-up text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight max-w-3xl mx-auto"
            style={{ animationDelay: "0.05s" }}
          >
            تحقق من أي دواء قبل استخدامه
          </h1>
          <p
            className="fade-up mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto"
            style={{ animationDelay: "0.15s" }}
          >
            ابحث باسم الدواء أو صوّر علبة الدواء لاستخراج الاسم. إذا كان الدواء
            مُدرجًا كمقيّد، سيتم تنبيهك وسنوجّهك لمراجعة الطبيب.
          </p>

          {/* Search Bar */}
          <div
            className="fade-up mt-10 w-full max-w-3xl mx-auto"
            style={{ animationDelay: "0.25s" }}
          >
            <div
              data-testid="search-bar"
              className="relative flex flex-col sm:flex-row items-stretch gap-3 bg-white p-3 rounded-2xl shadow-sm border-2 border-slate-200 focus-within:ring-4 focus-within:ring-slate-100 focus-within:border-slate-900 transition-all"
            >
              <div className="flex items-center flex-1 gap-2 px-2">
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input
                  data-testid="search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value.slice(0, 100))}
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  maxLength={100}
                  placeholder="اكتب اسم الدواء"
                  className="w-full bg-transparent border-none focus:outline-none text-lg sm:text-xl font-bold text-slate-900 py-2 placeholder:text-slate-300 placeholder:font-normal text-right"
                />
              </div>
              <button
                data-testid="photo-open-btn"
                onClick={() => setPhotoOcrOpen(true)}
                disabled={photoOcrOpen}
                className="flex items-center justify-center gap-2 shrink-0 h-14 sm:w-16 bg-slate-100 text-slate-800 rounded-xl hover:bg-slate-200 transition-colors font-bold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="تصوير علبة الدواء وقراءة الاسم"
                title="قراءة اسم الدواء من صورة العلبة"
              >
                <ImageIcon className="w-5 h-5" />
                <span className="sm:hidden">صورة</span>
              </button>
              <button
                data-testid="search-submit-btn"
                onClick={() => doSearch()}
                disabled={loading || scannerCooldown}
                className="flex items-center justify-center gap-2 shrink-0 px-6 h-14 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>جارٍ البحث</span>
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    <span>بحث</span>
                  </>
                )}
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              يمكنك البحث بالنص، أو تصوير علبة الدواء لاستخراج الاسم تلقائيًا.
            </p>
          </div>
        </div>
      </section>

      {/* Result Section */}
      <section id="search-result" className="max-w-5xl mx-auto px-6 py-12">
        {result && (
          <>
            {result.is_banned ? (
              <div className="space-y-6">
                {result.results.map((drug) => (
                  <BannedResultCard key={drug.id} drug={drug} />
                ))}
              </div>
            ) : (
              <>
                {result.suggestions?.length > 0 && (
                  <SuggestionsCard
                    query={result.query}
                    suggestions={result.suggestions}
                    onPick={(drug) => {
                      // Learn from correction (Feb 2026): fire-and-forget
                      // so the next user searching for the same typo goes
                      // straight to this drug without fuzzy or AI.
                      if (drug?.id && result.query) {
                        api.post("/drugs/aliases/learn", {
                          alias: result.query,
                          drug_id: drug.id,
                        }).catch(() => { /* silent — feature is best-effort */ });
                      }
                      openKnownDrug(drug);
                    }}
                  />
                )}
                <SafeResultCard query={result.query} />
              </>
            )}
          </>
        )}
      </section>

      {/* Recently added prohibited drugs */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-8 border-b-2 border-slate-900 pb-4">
          <div>
            <div className="text-sm font-bold tracking-tight text-slate-500 mb-2">
              قاعدة البيانات
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              قائمة الأدوية المقيّدة
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-slate-500 text-sm">
            <PillBottle className="w-4 h-4" />
            <span>{drugs.length} دواء</span>
          </div>
        </div>

        {drugs.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
            <p className="text-slate-500">لا توجد بيانات بعد.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {drugs.map((d, idx) => (
              <button
                key={d.id}
                data-testid={`drug-${d.id}`}
                onClick={() => openKnownDrug(d)}
                className="fade-up group text-right bg-white border-2 border-slate-200 hover:border-slate-900 rounded-2xl p-5 transition-all cursor-pointer"
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-red-50 text-red-600 border border-red-200 flex items-center justify-center">
                    <ScanLine className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-block text-xs font-bold tracking-tight bg-red-600 text-white px-2 py-0.5 rounded">
                        مقيد
                      </span>
                    </div>
                    <div className="text-lg font-bold text-slate-900 group-hover:text-slate-900 truncate">
                      {d.name}
                    </div>
                    {d.name_en && (
                      <div className="text-sm text-slate-500 truncate" dir="ltr">
                        {d.name_en}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2 leading-relaxed">
                      {d.reason}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-slate-500">
            © {new Date().getFullYear()} رقابة الأدوية للحفاظ على سلامتك.
          </div>
          <div className="text-xs text-slate-400">
            هذه المنصة لا تُغني عن استشارة الطبيب المختص.
          </div>
        </div>
      </footer>

      <PhotoOcrModal
        open={photoOcrOpen}
        onClose={() => setPhotoOcrOpen(false)}
        onConfirm={handlePhotoOcrConfirm}
      />
    </div>
  );
}
